import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { useAdminNavigation, useRestoreAdminListViewport } from "../../app/navigation/useAdminNavigation";
import { useAdminApplication, useAdminSession, useAdminUiStore } from "../../app/providers";
import { parseAdminListSearch, pathForAdminEntityScreen } from "../../app/routes";
import { FundingBalanceCard } from "../business-operations/FundingBalanceCard";
import { saveBlob } from "../workspace/behavior";
import { TransferActionDialog } from "./TransferActionDialog";
import { TransferListView } from "./components/TransferListView";
import { useTransferList } from "./queries";
import {
  parseTransferDate,
  parseTransferStatus,
} from "./schema";
import type { TransferSearchFormValues } from "./schema";

function transferSearchParams(options: {
  readonly page: number;
  readonly size: number;
  readonly filters: TransferSearchFormValues;
}): URLSearchParams {
  const params = new URLSearchParams({
    page: String(options.page),
    size: String(options.size),
  });
  if (options.filters.status) params.set("status", options.filters.status);
  if (options.filters.from) params.set("from", options.filters.from);
  if (options.filters.to) params.set("to", options.filters.to);
  return params;
}

export function TransferListPage() {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  const [exportError, setExportError] = useState("");
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const remembered = useAdminUiStore((state) => state.listContexts.transfers);
  const rememberListContext = useAdminUiStore((state) => state.rememberListContext);
  const list = parseAdminListSearch(searchParams, { page: 0, size: 20 });
  const status = parseTransferStatus(searchParams.get("status"));
  const purpose = "";
  const memberId = "";
  const [command,setCommand]=useState<{id:number;kind:"cancel"|"retry"}|null>(null);
  const [accountNumber,setAccountNumber]=useState(()=>remembered?.filters.accountNumber??"");
  const from = parseTransferDate(searchParams.get("from"));
  const parsedTo = parseTransferDate(searchParams.get("to"));
  const to = from && parsedTo && from > parsedTo ? "" : parsedTo;
  const [keyword, setKeyword] = useState(() => remembered?.filters.keyword ?? "");
  const filters = { status, purpose, keyword, memberId, from, to, accountNumber } satisfies TransferSearchFormValues;
  const queryInput = {
    ...filters,
    memberId: memberId ? Number(memberId) : undefined,
    page: list.page,
    size: list.size,
  };
  const query = useTransferList(queryInput);
  const lastPage = Math.max(0, Math.ceil((query.data?.totalCount ?? 0) / list.size) - 1);
  const pageOutOfRange = Boolean(query.data && list.page > lastPage);
  const navigation = useAdminNavigation({
    contextKey: "transfers",
    defaultListPath: "/transfers?page=0&size=20",
    originLabel: "송금 관리",
  });
  const restorationNotice = useRestoreAdminListViewport("transfers", Boolean(query.data && !pageOutOfRange));

  const remember = (next: {
    page: number;
    size: number;
    filters: TransferSearchFormValues;
  }, preserveViewport = false) => {
    const params = transferSearchParams(next);
    rememberListContext("transfers", {
      path: `${location.pathname}?${params.toString()}`,
      page: next.page,
      filters: { ...next.filters, size: String(next.size) },
    }, preserveViewport);
  };

  useEffect(() => {
    const canonical = transferSearchParams({ page: list.page, size: list.size, filters });
    if (canonical.toString() !== searchParams.toString()) {
      setSearchParams(canonical, { replace: true });
    }
  }, [filters.from, filters.memberId, filters.purpose, filters.status, filters.to, list.page, list.size, searchParams, setSearchParams]);

  useEffect(() => {
    if (!pageOutOfRange) return;
    const next = { page: lastPage, size: list.size, filters };
    remember(next, true);
    setSearchParams(transferSearchParams(next), { replace: true });
  }, [filters.from, filters.memberId, filters.purpose, filters.status, filters.to, keyword, lastPage, list.size, pageOutOfRange, setSearchParams]);

  const applySearch = (nextFilters: TransferSearchFormValues) => {
    const next = { page: 0, size: list.size, filters: nextFilters };
    setKeyword(nextFilters.keyword);
    setAccountNumber(nextFilters.accountNumber??"");
    remember(next);
    setSearchParams(transferSearchParams(next));
    navigation.focusListResults();
  };

  const changePage = (page: number) => {
    const next = { page, size: list.size, filters };
    remember(next);
    setSearchParams(transferSearchParams(next));
    navigation.focusListResults();
  };

  const viewTransfer = (transferId: number) => {
    remember({ page: list.page, size: list.size, filters });
    navigation.openDetail(pathForAdminEntityScreen("10", transferId), `transfer:${transferId}:detail`);
  };

  return (
    <>
    {exportError ? <p role="alert">{exportError}</p> : null}
    <TransferListView
      funding={<FundingBalanceCard purpose="CUSTOMER_PAYOUT"/>}
      onExport={session.authSession?.authorities?.includes("DATA_EXPORT") ? () => {
        setExportError("");
        void apiClient.get<Blob>("/api/v1/admin/bo/exports/TRANSFERS.xlsx", { query: Object.fromEntries(Object.entries(queryInput).filter(([, value]) => value !== "" && value != null)), responseType: "blob", cache: "no-store" })
          .then(({data}) => saveBlob(data, "carepass-transfers.xlsx")).catch(() => setExportError("엑셀 다운로드에 실패했습니다."));
      } : undefined}
      filters={filters}
      result={pageOutOfRange ? undefined : query.data}
      state={query.isError ? "error" : query.isPending || pageOutOfRange ? "loading" : "ready"}
      refreshing={query.isFetching && !query.isPending}
      notice={restorationNotice}
      detailHref={(transferId) => pathForAdminEntityScreen("10", transferId)}
      onSearch={applySearch}
      onPageChange={changePage}
      onRetry={() => { void query.refetch(); }}
      onCancel={row=>setCommand({id:row.id,kind:"cancel"})}
      onRetryTransfer={row=>setCommand({id:row.id,kind:"retry"})}
      onViewTransfer={viewTransfer}
    />
    {command?<TransferActionDialog key={`${command.id}:${command.kind}`} transferId={command.id} kind={command.kind} onClose={()=>setCommand(null)}/>:null}
    </>
  );
}
